import React, { useMemo } from 'react';
import { List } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountData } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { FilterMenu } from '../../common/filter/FilterMenu';
import { CheckboxGroupSection } from '../../common/filter/CheckboxGroupSection';
import { getActiveLocations } from '../../common/filter/locationGrouping';
import { useLanguage } from '../../common/GeneralUtils';
import { ALL_PASS_PAYMENT_STATUSES, ALL_PASS_STATUSES, ALL_PASS_TYPES, usePassesFilter } from './PassesFilterContext';
import { fromSentinel, toSentinel, UNDETERMINED_LOCATION_SENTINEL } from './locationFilterItems';
import { usePassesFilterSummary } from './usePassesFilterSummary';
import { SubscriptionsFilterSection } from './SubscriptionsFilterSection';

export const PassesFilter = () => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const summary = usePassesFilterSummary();
  const {
    statuses,
    setStatuses,
    paymentStatuses,
    setPaymentStatuses,
    passTypes,
    setPassTypes,
    orderPageIds,
    setOrderPageIds,
    locationIds,
    setLocationIds,
    isFilterActive,
    resetFilter,
  } = usePassesFilter();

  const activeLocations = useMemo(() => getActiveLocations(account), [account]);
  const showLocationFilter = activeLocations.length > 1;

  const locationItems: string[] = useMemo(
    () => [...activeLocations.map((l) => l.id), UNDETERMINED_LOCATION_SENTINEL],
    [activeLocations],
  );
  const selectedLocationItems: string[] = locationIds === null ? locationItems : locationIds.map(toSentinel);

  const locationLabel = (id: string): string => {
    if (id === UNDETERMINED_LOCATION_SENTINEL) return t('passes.filter.location_undetermined');
    const loc = activeLocations.find((l) => l.id === id);
    return loc?.name?.[language] || loc?.name?.en || t('passes.filter.location_unnamed');
  };

  return (
    <FilterMenu
      name="Passes"
      isActive={isFilterActive}
      summaryLabel={summary.primary}
      secondaryLabel={summary.secondary}
      onReset={resetFilter}>
      <List disablePadding>
        {showLocationFilter && (
          <CheckboxGroupSection
            title={t('passes.filter.location')}
            items={locationItems}
            itemLabel={locationLabel}
            selected={selectedLocationItems}
            onChange={(next) => setLocationIds(next.length === locationItems.length ? null : next.map(fromSentinel))}
          />
        )}

        <CheckboxGroupSection
          title={t('passes.filter.passType')}
          items={ALL_PASS_TYPES}
          itemLabel={(type) => t(`passes.pass_type.${type}`)}
          selected={passTypes}
          onChange={setPassTypes}
        />

        <CheckboxGroupSection
          title={t('passes.filter.status')}
          items={ALL_PASS_STATUSES}
          itemLabel={(status) => t(`passes.status.${status}`)}
          selected={statuses}
          onChange={setStatuses}
        />

        <SubscriptionsFilterSection account={account} selectedIds={orderPageIds} onChange={setOrderPageIds} />

        <CheckboxGroupSection
          title={t('passes.filter.paymentStatus')}
          items={ALL_PASS_PAYMENT_STATUSES}
          itemLabel={(status) => t(`passes.payment_status.${status}`)}
          selected={paymentStatuses}
          onChange={setPaymentStatuses}
          withDivider={false}
        />
      </List>
    </FilterMenu>
  );
};
